import { ReactNode } from 'react'
import { useHasModule } from '@/hooks/useOrganizationModules'
import { UpgradePrompt } from './UpgradePrompt'
import { Loader2 } from 'lucide-react'

interface FeatureGateProps {
  module: string
  children: ReactNode
  fallback?: ReactNode
  showUpgrade?: boolean
  upgradeMessage?: string
}

/**
 * Component that conditionally renders children based on module access.
 *
 * @example
 * <FeatureGate module="certificate-manager">
 *   <CertificateUpload />
 * </FeatureGate>
 *
 * @example With custom upgrade prompt
 * <FeatureGate
 *   module="bom-professional"
 *   showUpgrade
 *   upgradeMessage="Unlock advanced BOM features"
 * >
 *   <BOMTreeView />
 * </FeatureGate>
 */
export function FeatureGate({
  module,
  children,
  fallback = null,
  showUpgrade = false,
  upgradeMessage,
}: FeatureGateProps) {
  const { hasModule, isLoading } = useHasModule(module)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!hasModule) {
    if (showUpgrade) {
      return <UpgradePrompt moduleId={module} message={upgradeMessage} />
    }
    return <>{fallback}</>
  }

  return <>{children}</>
}
